import * as React from "react";
import { react2Angular } from "~/Core/React2Angular";
import { TS } from "~/Services/TS";
import { ImagePickerControl, PropertyDefinition } from "~/Types/PropertyDefinition";
import { useFileBrowser } from "~/Components/EntityForm/FileBrowser";
import * as qs from "qs";
import { useEffect, useState } from "react";

export const ImagePicker = (props: {
    controlType: ImagePickerControl;
    propertyDefinition: PropertyDefinition;
    value: string;
    onChange: (name: string, value: string, isCustomProperty: boolean) => void;
    $rootScope: AngularAny;
}) => {
    const { isCustomProperty, isRequired, canEdit, name, label, fieldName } = props.propertyDefinition;
    const { canPreview, validFileExtensions, validImageSizes } = props.controlType;
    const [isExtensionValid, setIsExtensionValid] = useState(true);
    const [isSizeValid, setIsSizeValid] = useState(true);
    const [imageCanBeLoaded, setImageCanBeLoaded] = useState(true);
    const [height, setHeight] = useState(0);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        if (!props.value) {
            return;
        }
        const index = props.value.indexOf("?");
        if (index < 0) {
            return;
        }
        const queryValues: { height?: string; width?: string } = qs.parse(props.value.substring(index + 1));
        if (queryValues.height) {
            setHeight(parseInt(queryValues.height, 10));
        }
        if (queryValues.width) {
            setWidth(parseInt(queryValues.width, 10));
        }
    }, []);

    useEffect(() => {
        if (height && width && props.value.length > 0 && validImageSizes.length > 0) {
            if (height !== width) {
                setIsSizeValid(false);
                return;
            }

            const validSizes = validImageSizes.split("|").map(o => parseInt(o, 10));
            setIsSizeValid(validSizes.includes(height));
        } else {
            setIsSizeValid(true);
        }
    }, [props.value, validImageSizes, height, width]);

    useEffect(() => {
        if (props.value.length > 0 && validFileExtensions.length > 0) {
            let extension = props.value.substring(props.value.lastIndexOf(".") + 1).toLowerCase();
            // dam assets have the form domain/assets/image.png/uglyhash so we need to remove /uglyhash to see the extension
            if (extension.indexOf("/") > 0) {
                extension = extension.substring(0, extension.indexOf("/"));
            }
            setIsExtensionValid(validFileExtensions.toLowerCase().split("|").includes(extension));
        }
    }, [props.value, validFileExtensions]);

    const preview = () => {
        props.$rootScope.$broadcast("showImagePickerPreview", { path: props.value });
    };

    const onFileSelected = (url: string) => {
        updateFieldWithWidthAndHeight(url);

        props.onChange(name, url, isCustomProperty);
    };

    const updateFieldWithWidthAndHeight = (url: string) => {
        if (!url) {
            setWidth(0);
            setHeight(0);
            setImageCanBeLoaded(false);
        }

        const image = new Image();
        image.src = url;
        image.onerror = () => {
            setWidth(0);
            setHeight(0);
            setImageCanBeLoaded(false);
        };
        image.onload = (event: any) => {
            const { width, height } = event.currentTarget;
            setImageCanBeLoaded(true);
            setWidth(width);
            setHeight(height);
            if (!width || !height || fieldName === "website_websiteFavicon") {
                return;
            }

            let newUrl = `${url}?width=${width}&height=${height}`;

            const index = url.indexOf("?");
            if (index >= 0) {
                const nextQueryString = qs.parse(url.substring(index + 1));
                nextQueryString.width = width;
                nextQueryString.height = height;
                newUrl = `${url.slice(0, index)}?${qs.stringify(nextQueryString)}`;
            }

            props.onChange(name, newUrl, isCustomProperty);
        };
    };

    const { openFileBrowser } = useFileBrowser(onFileSelected);

    const prettyPrintArray = (value: string[]) => {
        let result = "";
        for (let x = 0; x < value.length; x++) {
            result += value[x];
            if (x + 2 < value.length) {
                result += ", ";
            } else if (x + 1 < value.length) {
                result += " or ";
            }
        }
        return result;
    };

    const prettyPrintExtensions = () => {
        const extensions = validFileExtensions.toUpperCase().split("|");
        return prettyPrintArray(extensions);
    };

    const prettyPrintSizes = () => {
        const sizes = validImageSizes
            .toUpperCase()
            .split("|")
            .map(o => o + "x" + o);
        return prettyPrintArray(sizes);
    };

    return (
        <div className="file-picker medium-11">
            <span className="inline-label">
                <input
                    type="text"
                    id={fieldName}
                    name={fieldName}
                    className="file-picker__file-path"
                    autoComplete="off"
                    required={isRequired}
                    value={props.value}
                    onChange={event => props.onChange(name, event.currentTarget.value, isCustomProperty)}
                    onBlur={event => onFileSelected(event.currentTarget.value)}
                    disabled={!canEdit}
                />
                <button
                    type="button"
                    className="button tertiary file-picker__browse-button"
                    onClick={() => openFileBrowser()}
                    disabled={!canEdit}
                >
                    {TS("Browse")}
                </button>
                {canPreview && (
                    <button
                        type="button"
                        className="button tertiary"
                        onClick={preview}
                        disabled={!props.value || props.value.length == 0 || !imageCanBeLoaded}
                    >
                        {TS("Preview")}
                    </button>
                )}
            </span>
            {width !== 0 && height !== 0 && fieldName !== "website__websiteFavicon" && (
                <div className="dimensions-text">
                    <div>{TS("Image Dimensions")}</div>
                    <div className="grid-block">
                        <span>{TS("Height: {0}px", String(height))}</span>
                        <span>{TS("Width: {0}px", String(height))}</span>
                    </div>
                </div>
            )}
            <div className="warning-messages">
                {!imageCanBeLoaded && props.value.length > 0 && (
                    <span>
                        <i className="fa-light fa-triangle-exclamation"></i>{" "}
                        {TS("Image cannot be located, please verify image path.")}
                    </span>
                )}
            </div>
            <div className="error-messages">
                {!isSizeValid && (
                    <span id={`tst_${fieldName}_imageSizeValidator`}>
                        <i className="fa-light fa-triangle-exclamation"></i>
                        {TS("{0} must be {1} pixels.", label, prettyPrintSizes())}
                    </span>
                )}
                {!isExtensionValid && (
                    <span id={`tst_${fieldName}_imageExtensionValidator`}>
                        <i className="fa-light fa-triangle-exclamation"></i>
                        {TS("{0} must be {1} file format.", label, prettyPrintExtensions())}
                    </span>
                )}
            </div>
        </div>
    );
};

angular
    .module("insite-admin")
    .component(
        "imagePicker",
        react2Angular(
            ImagePicker,
            ["serializedPropertyDefinition", "serializedControlType", "value", "onChange"],
            ["$rootScope"],
        ),
    );
